// src/pages/customer/CareerDetail.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { getJobById, submitJobApplication } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiArrowLeft, FiMapPin, FiClock, FiCalendar, FiTag, FiBriefcase } from "react-icons/fi";

export default function CareerDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    getJobById(id).then((j) => { setJob(j); setLoading(false); });
  }, [id]);

  const onApply = async (data) => {
    setSubmitting(true);
    try {
      await submitJobApplication(id, data);
      toast.success("Application submitted! We'll get back to you within 5 business days.");
      reset();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner center />;

  if (!job) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <Helmet>
        <title>Job Not Found | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>
      <p className="text-5xl mb-4">🌿</p>
      <h2 className="text-xl font-semibold text-gray-700 mb-2">Job listing not found</h2>
      <p className="text-gray-400 text-sm mb-6">It may have been filled or removed.</p>
      <Link to="/careers" className="btn-primary inline-flex items-center gap-2">
        <FiArrowLeft className="w-4 h-4" /> Back to Careers
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{job.title ? `${job.title} | Careers | Tripura Agro` : "Tripura Agro Careers"}</title>
        <meta
          name="description"
          content={job.description?.slice(0, 160) || "Explore current job openings at Tripura Agro Industries."}
        />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

        {/* ── Back link ── */}
        <Link
          to="/careers"
          className="inline-flex items-center gap-2 text-sm font-semibold mb-6"
          style={{ color: "#3a5c2f" }}
        >
          <FiArrowLeft className="w-4 h-4" /> Back to all openings
        </Link>

        {/* ── Title ── */}
        <h1 className="font-display text-2xl sm:text-3xl font-black text-gray-900 leading-tight mb-4"
          style={{ fontFamily: "var(--custom-body-font-family)" }}>
          {job.title}
        </h1>

        {/* ── Pill meta row ── */}
        <div className="flex flex-wrap gap-2.5 mb-8">
          {job.department && <Pill icon={<FiBriefcase size={13} />} text={job.department} />}
          {job.location && <Pill icon={<FiMapPin size={13} />} text={job.location} />}
          {job.type && <Pill icon={<FiClock size={13} />} text={job.type} />}
          {job.postedAt && (
            <Pill
              icon={<FiCalendar size={13} />}
              text={`Posted: ${new Date(job.postedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}
            />
          )}
        </div>

        {/* ── About the role ── */}
        {job.description && (
          <div className="mb-8">
            <h2 className="font-display text-lg font-bold text-gray-900 mb-3">About the Role</h2>
            <p className="text-gray-500 text-[14px] sm:text-md leading-relaxed ">
              {job.description}
            </p>
          </div>
        )}

        {/* ── Requirements ── */}
        {job.requirements && (
          <div className="mb-10">
            <h2 className="font-display text-lg font-bold text-gray-900 mb-3">Requirements</h2>
            <p className="text-gray-500 text-[15px] sm:text-md leading-relaxed ">
              {job.requirements}
            </p>
          </div>
        )}

        {/* ── Application form ── */}
        <div className="rounded-2xl border p-5 sm:p-7" style={{ borderColor: "#e2dcc8", background: "#f7f3e3" }}>
          <h2 className="font-display text-lg font-bold text-gray-900 mb-1">Apply for this role</h2>
          <p className="text-sm text-gray-500 mb-6">We'll get back to you within 5 business days.</p>

          <form onSubmit={handleSubmit(onApply)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Full Name</label>
                <input
                  {...register("fullName", { required: "Full name is required" })}
                  className={`input bg-white ${errors.fullName ? "border-red-400" : ""}`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName.message}</p>}
              </div>
              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  {...register("email", { required: "Email is required" })}
                  className={`input bg-white ${errors.email ? "border-red-400" : ""}`}
                  placeholder="you@example.com"
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div>
              <label className="label">Phone Number</label>
              <input
                {...register("phone", { required: "Phone number is required" })}
                className={`input bg-white ${errors.phone ? "border-red-400" : ""}`}
                placeholder="+91 98765 43210"
              />
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
            </div>

            <div>
              <label className="label">Resume Link</label>
              <input
                {...register("resumeLink", { required: "Resume link is required" })}
                className={`input bg-white ${errors.resumeLink ? "border-red-400" : ""}`}
                placeholder="https://drive.google.com/..."
              />
              <p className="text-xs text-gray-400 mt-1">Paste a shareable link to your resume (Google Drive, Dropbox, etc.)</p>
              {errors.resumeLink && <p className="text-xs text-red-500 mt-1">{errors.resumeLink.message}</p>}
            </div>

            <div>
              <label className="label">Cover Note (Optional)</label>
              <textarea
                {...register("coverNote")}
                rows={4}
                className="input bg-white resize-none"
                placeholder="Tell us why you'd be a great fit for this role..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-auto flex items-center justify-center gap-2 uppercase tracking-wide text-sm"
            >
              {submitting ? <><Spinner size="sm" /> Submitting...</> : "Submit Application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Pill({ icon, text }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
      style={{ background: "#f0f5ea", color: "#3a5c2f" }}
    >
      {icon} {text}
    </span>
  );
}